import React, { Fragment } from 'react';
import { Button, InputGroupText } from 'reactstrap';
import TextInput from '../components/common/forms/input/TextInput';
import EnumStatus from '../components/common/EnumStatus';
import {
  isRequired,
  validateDynamicFormDuplicateElements,
  validateDynamicFormDuplicateTextElements,
  validateIntegerNumber,
  validateLength,
  validateMinNumber,
  validateNumber,
} from '../utils/validators';
import CargoCategorySelect from '../components/common/forms/select/CargoCategorySelect';
import CommonProductSelectCargoForm from '../components/common/forms/select/CommonProductSelectCargoForm';
import DriverSelect from '../components/common/forms/select/DriverSelect';
import DriverTypeSelect from '../components/common/forms/select/DriverTypeSelect';
import CargoItemSelect from '../components/common/forms/select/CargoItemSelect';
import PrintTemplateSelect from '../components/common/forms/select/PrintTemplateSelect';
import DatePicker from '../components/common/forms/input/DatePicker';
import Select from '../components/common/forms/select/Select';
import { CURRENCY, DATE_TIME_FORMAT } from './locale';
import LiquidationSessionSelect from '../components/common/forms/select/LiquidationSessionSelect';
import { DepositForm } from '../components/units/sales/deposit/DepositForm';
import AgencySelect from '../components/common/forms/select/AgencySelect';
import ItemCategorySelect from '../components/common/forms/select/ItemCategorySelect';
import { tzNormalizeDate } from '../utils/date';
import TollSelect from '../components/common/forms/select/TollSelect';
import DriverDocumentTypeSelect from '../components/common/forms/select/DriverDocumentTypeSelect';
import DriverContractTypeSelect from '../components/common/forms/select/DriverContractTypeSelect';
import RegisteredBusSelect from '../components/common/forms/select/RegisteredBusSelect';
import ContractMovementTypeSelect from '../components/common/forms/select/ContractMovementTypeSelect';
import RouteSelect from '../components/common/forms/select/RouteSelect';
import ServiceTypeSelect from '../components/common/forms/select/ServiceTypeSelect';
import MaterialSelect from '../components/common/forms/select/MaterialSelect';
import { enumToSelectOptions } from '../utils/enum';
import {
  ACTIVITY_STATUS,
  BILLING_DOCUMENT_TYPE,
  CARGO_CUSTOMER_TYPE,
  DISCOUNT_CODE_FLOOR_RULE,
  ITEM_CONTENT_TYPE,
  SENDER_REMISSION_GUIDE_CLOSURE_STATUS,
  SENDER_REMISSION_GUIDE_ITEM_PICKUP_STATUS,
  FOOD_PUBLIC_TYPE,
  EXCLUSIVE_TRANSACTION_CODES,
  EXCESS_BAGGAGE_INCREMENTAL_PRICE,
  TRANSACTION_TYPE_CREDIT_NOTE,
  TRANSACTION_TYPE_OTHER,
  TRANSACTION_TYPE_CREDIT_CARD,
  TRANSACTION_TYPE_BANK_TRANSFER,
  BBVA_BANK_ID,
  DEPOSIT_MIGRATION_STATUS,
} from './constants';
import InternalCommonProductSelect from '../components/common/forms/select/InternalCommonProductSelect';
import MaintenanceWarningSelect from '../components/common/forms/select/MaintenanceWarningSelect';
import CitySelect from '../components/common/forms/select/CitySelect';
import TimeInput from '../components/common/forms/input/TimeInput';
import DocumentTypeSelect from '../components/common/forms/select/DocumentTypeSelect';
import MaintenanceTaskSelect from '../components/common/forms/select/MaintenanceTaskSelect';
import ActivitySelect from '../components/common/forms/select/ActivitySelect';
import MechanicSelect from '../components/common/forms/select/MechanicSelect';
import ActivityMaintenancePlanningSelect from '../components/common/forms/select/ActivityMaintenancePlanningSelect';
import CustomerSelect from '../components/common/forms/select/CustomerSelect';
import CabinCrewDocumenTypesSelect from '../components/common/forms/select/CabinCrewDocumenTypesSelect';
import CabinCrewContractTypesSelect from '../components/common/forms/select/CabinCrewContractTypesSelect';
import SegmentSelect from '../components/common/forms/select/SegmentSelect';
import LocationSelect from '../components/common/forms/select/LocationSelect';
import DistrictPostalCodeSelect from '../components/common/forms/select/DistrictPostalCodeSelect';
import DiscountCodeAuthorizerSelect from '../components/common/forms/select/DiscountCodeAuthorizerSelect';
import BankAccountSelect from '../components/common/forms/select/BankAccountSelect';
import OrganizationChartSelect from '../components/common/forms/select/OrganizationChartSelect';
import BusMaintenanceStayDateField from '../components/units/mechanical-maintenance/bus-maintenance-stay/BusMaintenanceStayDateField';
import FileInput from '../components/common/forms/input/FileInput';
import IncidentNodeSelectorDynamicField from '../components/units/supervision-control/itinerary-incident/IncidentNodeSelectorDynamicField';
import TicketBaggageSelect from '../components/common/forms/select/TicketBaggageSelect';
import ItemConditionSelect from '../components/common/forms/select/ItemConditionSelect';
import FoodSelect from '../components/common/forms/select/FoodSelect';
import SupplierSelect from '../components/common/forms/select/SupplierSelect';
import CompanyResourceSelect from '../components/common/forms/select/CompanyResourceSelect';
import { getRowTransactionCode } from '../utils/units/paymentSplit';
import CreditNoteSelect from '../components/common/forms/select/CreditNoteSelect';
import FreeOfChargeCategorySelect from '../components/common/forms/select/FreeOfChargeCategorySelect';
import SupplierFoodSelect from '../components/common/forms/select/SupplierFoodSelect';
import CabinCrewSelect from '../components/common/forms/select/CabinCrewSelect';
import PdfFileInput from '../components/common/forms/input/PdfFileInput';

const generateCargoItemColumns = (
  onQuantityInputChange,
  onCommonProductChange,
  onWeightChange,
  onChangeCategory,
) => [
  {
    lg: 1,
    md: 2,
    sm: 12,
    label: 'Cantidad',
    requiredLabel: true,
    component: TextInput,
    name: 'quantity',
    type: 'text',
    onChange: onQuantityInputChange,
    validate: [isRequired, validateNumber, validateIntegerNumber],
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Producto Frecuente',
    component: CommonProductSelectCargoForm,
    name: 'commonProductId',
    onChange: onCommonProductChange,
    otherProps: { cleanWhenUnmount: false },
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Categoría',
    requiredLabel: true,
    component: CargoCategorySelect,
    name: 'cargoCategoryId',
    otherProps: {
      cleanWhenUnmount: false,
      isDisabled: (row) => Boolean(row && row.isCommonProductSelected),
    },
    onChange: onChangeCategory,
    validate: [isRequired],
  },
  {
    lg: 3,
    md: 4,
    sm: 12,
    label: 'Descripción',
    requiredLabel: true,
    component: TextInput,
    name: 'description',
    type: 'text',
    validate: [isRequired],
  },
  {
    lg: 1,
    md: 1,
    sm: 12,
    label: 'Peso',
    requiredLabel: true,
    component: TextInput,
    name: 'weight',
    type: 'text',
    onChange: onWeightChange,
    validate: [validateNumber],
    disabled: (row) => Boolean(row && row.isCommonProductSelected),
  },
  {
    lg: 1,
    md: 1,
    sm: 12,
    name: 'length',
    label: 'Largo (cm)',
    component: TextInput,
    type: 'text',
    validate: [validateNumber],
    disabled: (row) =>
      Boolean(row && (row.isCommonProductSelected || row.isEnvelope)),
  },
  {
    lg: 1,
    md: 1,
    sm: 12,
    name: 'width',
    label: 'Ancho (cm)',
    component: TextInput,
    type: 'text',
    validate: [validateNumber],
    disabled: (row) =>
      Boolean(row && (row.isCommonProductSelected || row.isEnvelope)),
  },
  {
    lg: 1,
    md: 1,
    sm: 12,
    name: 'height',
    label: 'Alto (cm)',
    component: TextInput,
    type: 'text',
    validate: [validateNumber],
    disabled: (row) =>
      Boolean(row && (row.isCommonProductSelected || row.isEnvelope)),
  },
  {
    lg: 1,
    md: 2,
    sm: 12,
    label: 'Perecible',
    name: 'perishable',
    component: 'input',
    type: 'checkbox',
    otherProps: { className: 'mt-4' },
  },
  {
    lg: 1,
    md: 2,
    sm: 12,
    label: 'P. Venta',
    requiredLabel: true,
    component: TextInput,
    name: 'salePrice',
    type: 'text',
    otherProps: { disabled: true },
    validate: [isRequired, validateNumber],
  },
];

const generateInternalTransferItemColumns = (
  onQuantityInputChange,
  onCommonProductChange,
  onWeightChange,
  disabled = false,
) => [
  {
    lg: 2,
    md: 2,
    sm: 12,
    label: 'Cantidad',
    requiredLabel: true,
    component: TextInput,
    name: 'quantity',
    type: 'text',
    onChange: onQuantityInputChange,
    validate: [isRequired, validateNumber],
    otherProps: { disabled },
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Producto Frecuente',
    component: InternalCommonProductSelect,
    name: 'commonProductId',
    onChange: onCommonProductChange,
    otherProps: { cleanWhenUnmount: false, isDisabled: disabled },
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Categoría',
    component: CargoCategorySelect,
    requiredLabel: true,
    name: 'cargoCategoryId',
    otherProps: { cleanWhenUnmount: false, isDisabled: disabled },
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 4,
    sm: 12,
    label: 'Descripción',
    requiredLabel: true,
    component: TextInput,
    name: 'description',
    type: 'text',
    validate: [isRequired],
    otherProps: { disabled },
  },
  {
    lg: 1,
    md: 2,
    sm: 12,
    label: 'Peso',
    component: TextInput,
    name: 'weight',
    type: 'text',
    onChange: onWeightChange,
    validate: [validateNumber],
  },
];

const generateManualGrrColumns = (disabled, showExtraColumn) => {
  const columns = [
    {
      lg: 1,
      md: 2,
      sm: 12,
      label: 'Cantidad',
      requiredLabel: true,
      component: TextInput,
      name: 'quantity',
      type: 'text',
      validate: [isRequired, validateNumber],
      disabled: () => !!disabled,
    },
    {
      lg: 3,
      md: 4,
      sm: 12,
      label: 'Descripción',
      requiredLabel: true,
      component: TextInput,
      name: 'description',
      type: 'text',
      validate: [isRequired],
      disabled: () => !!disabled,
    },
    {
      lg: 1,
      md: 2,
      sm: 12,
      label: 'Peso',
      requiredLabel: true,
      component: TextInput,
      name: 'weight',
      type: 'text',
      validate: [isRequired, validateNumber],
      disabled: () => !!disabled,
    },
  ];

  if (showExtraColumn) {
    columns.push({
      lg: 2,
      md: 3,
      sm: 12,
      label: 'Área de la Empresa',
      requiredLabel: true,
      component: OrganizationChartSelect,
      name: 'companyArea',
      validate: [isRequired],
      disabled: () => !!disabled,
    });
  }

  return columns;
};

const generateDriverAssignmentColumns = (onDriverChange) => [
  {
    lg: 7,
    md: 7,
    sm: 12,
    label: 'Conductor',
    requiredLabel: true,
    component: DriverSelect,
    onChange: onDriverChange,
    name: 'driver',
    validate: [isRequired],
    otherProps: { cleanWhenUnmount: false },
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Tipo de Conductor',
    requiredLabel: true,
    component: DriverTypeSelect,
    name: 'driverType',
    validate: [isRequired],
    otherProps: { cleanWhenUnmount: false },
  },
];

const generateCargoItemToAgencyColumns = (onCargoItemChange) => [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Correlación de Item',
    requiredLabel: true,
    component: CargoItemSelect,
    onChange: onCargoItemChange,
    name: 'item',
    validate: [isRequired],
    otherProps: { cleanWhenUnmount: false, isClearable: false },
  },
];

const PRINT_STATION_FORM_COLUMNS = [
  {
    lg: 4,
    md: 4,
    sm: 4,
    label: 'Plantilla de Impresión',
    requiredLabel: true,
    component: PrintTemplateSelect,
    name: 'printTemplateType',
    validate: [isRequired],
    otherProps: { cleanWhenUnmount: false, isClearable: false },
  },
  {
    lg: 3,
    md: 6,
    sm: 3,
    label: 'Serie',
    requiredLabel: true,
    component: TextInput,
    name: 'documentSeries',
    validate: [isRequired],
    type: 'text',
  },
  {
    lg: 4,
    md: 4,
    sm: 4,
    label: 'Correlativo',
    requiredLabel: true,
    component: TextInput,
    name: 'documentCode',
    validate: [isRequired],
    type: 'text',
  },
];

const PRICE_PER_RANGE_DATES_FORM_COLUMNS = [
  {
    lg: 4,
    md: 4,
    sm: 4,
    label: 'De',
    requiredLabel: true,
    component: DatePicker,
    name: 'startDate',
  },
  {
    lg: 4,
    md: 4,
    sm: 4,
    label: 'Hasta',
    requiredLabel: true,
    component: DatePicker,
    name: 'endDate',
  },
  {
    lg: 3,
    md: 3,
    sm: 3,
    label: 'Diferencia',
    requiredLabel: true,
    component: TextInput,
    name: 'absoluteChange',
  },
  {
    lg: 1,
    md: 1,
    sm: 1,
    component: TextInput,
    name: 'id',
    type: 'hidden',
  },
];

const generatePriceVariationByZonesColumns = (
  floorOptions,
  onClickStart,
  onClickEnd,
) => [
  {
    lg: 6,
    md: 6,
    sm: 6,
    label: 'Piso',
    requiredLabel: true,
    component: Select,
    name: 'itemfloorNumber',
    validate: [isRequired],
    otherProps: { options: floorOptions, isClearable: false },
  },
  {
    lg: 6,
    md: 6,
    sm: 6,
    label: 'Precio Base',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'basePriceOverride',
    validate: [isRequired, validateNumber],
    otherProps: { append: <InputGroupText>{CURRENCY}</InputGroupText> },
  },
  {
    lg: 4,
    md: 12,
    sm: 4,
    label: 'Arriba Izq.',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'startXPosition',
    validate: [isRequired, validateIntegerNumber],
    otherProps: { append: <InputGroupText>(x)</InputGroupText> },
  },
  {
    lg: 4,
    md: 12,
    sm: 4,
    label: 'Arriba Izq.',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'startYPosition',
    validate: [isRequired, validateIntegerNumber],
    otherProps: { append: <InputGroupText>(y)</InputGroupText> },
  },
  {
    lg: 4,
    md: 12,
    sm: 4,
    name: 'xPositionButton',
    customizedField: 'button',
    label: 'Seleccionar',
    onClick: onClickStart,
  },
  {
    lg: 4,
    md: 12,
    sm: 4,
    label: 'Abajo Der.',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'endXPosition',
    validate: [isRequired, validateIntegerNumber],
    otherProps: { append: <InputGroupText>(x)</InputGroupText> },
  },
  {
    lg: 4,
    md: 12,
    sm: 4,
    label: 'Abajo Der.',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'endYPosition',
    validate: [isRequired, validateIntegerNumber],
    otherProps: { append: <InputGroupText>(y)</InputGroupText> },
  },
  {
    lg: 4,
    md: 12,
    sm: 4,
    name: 'yPositionButton',
    customizedField: 'button',
    label: 'Seleccionar',
    onClick: onClickEnd,
  },
  {
    lg: 4,
    md: 12,
    sm: 4,
    name: 'promotionQuantity',
    label: 'Cantidad de Asientos por Promoción',
    component: TextInput,
    validate: [validateNumber],
  },
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Asientos por Zona',
    name: `priceZoneSegmentCityList`,
    isFieldArray: true,
    fields: [
      {
        name: 'sourceCity',
        label: 'Origen',
        component: CitySelect,
        requiredLabel: true,
        validate: [isRequired],
      },
      {
        name: 'destinationCity',
        label: 'Destino',
        component: CitySelect,
        requiredLabel: true,
        validate: [isRequired],
      },
      {
        name: 'promotionBasePrice',
        label: 'Precio Base Promocional',
        component: TextInput,
        validate: [isRequired, validateNumber],
        requiredLabel: true,
      },
    ],
  },
];

const NOTES_FORM_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Nota',
    requiredLabel: true,
    component: TextInput,
    name: 'note',
    validate: [isRequired],
  },
];

const DAILY_LIQUIDATION_FORM_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Liquidación',
    requiredLabel: true,
    component: LiquidationSessionSelect,
    name: 'dailyLiquidationSession',
    validate: [isRequired, DepositForm.hasValidLiquidation],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
      query: { deposited: false },
    },
  },
];

const AGENCY_GROUP_FORM_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Agencia',
    requiredLabel: true,
    component: AgencySelect,
    name: 'agency',
    validate: [
      isRequired,
      ({ value }, allValues) =>
        validateDynamicFormDuplicateElements(
          value,
          allValues.agencyList,
          'agency',
        ),
    ],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
    },
  },
];

const AGENCY_BANK_CODE_FORM_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Código',
    requiredLabel: true,
    component: TextInput,
    name: 'agencyCode',
    placeholder: 'Código de Banco',
    validate: [
      isRequired,
      (value, allValues) =>
        validateDynamicFormDuplicateTextElements(
          value,
          allValues.agencyBankCodeList,
          'agencyCode',
        ),
    ],
  },
];

const EXPENSE_STATUS_REMARK_FORM_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Nota',
    requiredLabel: true,
    component: TextInput,
    type: 'textarea',
    name: 'remarks',
    validate: [isRequired],
  },
];

const generateBaggageTicketFormColumns = (onChangeTicket, disabled) => [
  {
    md: 2,
    label: 'Boleto',
    requiredLabel: true,
    component: TicketBaggageSelect,
    onChange: onChangeTicket,
    name: 'ticket',
    validate: [isRequired],
    otherProps: {
      isDisabled: disabled,
    },
  },
  {
    md: 2,
    label: 'Pasajero',
    name: 'customerFullName',
    dependentKey: 'ticket',
  },
  {
    md: 2,
    label: 'Doc. Identidad',
    name: 'customerDocumentNumber',
    dependentKey: 'ticket',
  },
  {
    md: 2,
    label: 'Origen',
    name: 'sourceLocationName',
    dependentKey: 'ticket',
  },
  {
    md: 2,
    label: 'Destino',
    name: 'destinationLocationName',
    dependentKey: 'ticket',
  },
  {
    md: 2,
    label: 'Fecha',
    name: 'departureDate',
    dependentKey: 'ticket',
    Cell: (value) => tzNormalizeDate({ date: value, format: DATE_TIME_FORMAT }),
  },
];

const generateBaggageItemFormColumns = (
  disabled,
  onWeightChange,
  onIncrementalPriceChange,
) => [
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Tipos de Equipaje',
    requiredLabel: true,
    component: ItemCategorySelect,
    name: 'itemCategoryId',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
      isDisabled: disabled,
      isRegular: true,
      sortByName: true,
    },
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Contenido',
    name: 'itemContent',
    component: Select,
    validate: [isRequired],
    requiredLabel: true,
    otherProps: {
      options: Object.values(ITEM_CONTENT_TYPE).map((item) => item),
    },
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Descripción',
    requiredLabel: true,
    component: TextInput,
    name: 'description',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Estado',
    requiredLabel: true,
    component: ItemConditionSelect,
    name: 'itemCondition',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
      isDisabled: disabled,
      isMulti: true,
      isRegular: true,
    },
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Observaciones',
    component: TextInput,
    name: 'conditionDescription',
    type: 'text',
    hidden: (rowValues) =>
      !rowValues ||
      !rowValues.itemCondition ||
      !rowValues.itemCondition.some((condition) => condition.requiredNote),
    validate: [isRequired],
    requiredLabel: true,
  },
  {
    lg: 1,
    md: 3,
    sm: 12,
    label: 'Peso (kg)',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'weight',
    validate: [isRequired, validateNumber],
    otherProps: { disabled },
    onChange: onWeightChange,
  },
  {
    lg: 1,
    md: 3,
    sm: 12,
    label: 'Exceso (kg)',
    name: 'exceededWeight',
    component: TextInput,
    type: 'text',
    otherProps: { disabled: true },
    hidden: (rowValues) => !rowValues || !rowValues.exceededWeight,
    validate: [isRequired],
    requiredLabel: true,
  },
  {
    lg: 1,
    md: 3,
    sm: 12,
    label: 'Precio exceso',
    name: 'excessPrice',
    component: TextInput,
    type: 'text',
    otherProps: { disabled: true },
    hidden: (rowValues) => !rowValues || !rowValues.exceededWeight,
    validate: [isRequired],
    requiredLabel: true,
  },
  {
    lg: 1,
    md: 3,
    sm: 12,
    label: 'IP',
    name: 'incrementalPrice',
    component: Select,
    otherProps: {
      options: EXCESS_BAGGAGE_INCREMENTAL_PRICE,
      isClearable: true,
      isDisabled: disabled,
      placeholder: 'IP',
    },
    onChange: onIncrementalPriceChange,
  },
];

const generateBaggageCargoItemColumns = (disabled, onWeightChange) => [
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Tipos de Equipaje',
    requiredLabel: true,
    component: ItemCategorySelect,
    name: 'itemCategoryId',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
      isDisabled: disabled,
      isSpecial: true,
      sortByName: true,
    },
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Contenido',
    name: 'itemContent',
    component: Select,
    otherProps: {
      options: Object.values(ITEM_CONTENT_TYPE).map((item) => item),
    },
    validate: [isRequired],
    requiredLabel: true,
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Descripción',
    requiredLabel: true,
    component: TextInput,
    name: 'description',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Estado',
    requiredLabel: true,
    component: ItemConditionSelect,
    name: 'itemCondition',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
      isDisabled: disabled,
      isMulti: true,
      isSpecial: true,
    },
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Observaciones',
    component: TextInput,
    name: 'conditionDescription',
    type: 'text',
    hidden: (rowValues) =>
      !rowValues ||
      !rowValues.itemCondition ||
      !rowValues.itemCondition.some((condition) => condition.requiredNote),
    validate: [isRequired],
    requiredLabel: true,
  },
  {
    lg: 1,
    md: 3,
    sm: 12,
    label: 'Peso (kg)',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'weight',
    validate: [isRequired, validateNumber],
    otherProps: { disabled },
    onChange: onWeightChange,
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Precio',
    name: 'unitPrice',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    validate: [isRequired],
    otherProps: { disabled: true },
  },
];

const TOLL_FORM_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Peaje',
    requiredLabel: true,
    component: TollSelect,
    name: 'toll',
    validate: [
      isRequired,
      ({ value }, allValues) =>
        validateDynamicFormDuplicateElements(value, allValues.tollList, 'toll'),
    ],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
    },
  },
];

const DRIVER_DOCUMENT_FORM_COLUMNS = [
  {
    lg: 3,
    md: 3,
    sm: 6,
    label: 'Tipo de Documento',
    requiredLabel: true,
    component: DriverDocumentTypeSelect,
    name: 'driverDocumentTypeId',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
    },
  },
  {
    lg: 2,
    md: 3,
    sm: 6,
    label: 'Número de Documento',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'documentNumber',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 3,
    sm: 6,
    label: 'Fecha de Expedición',
    requiredLabel: true,
    component: DatePicker,
    name: 'effectiveDate',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 3,
    sm: 6,
    label: 'Fecha de Expiración',
    requiredLabel: true,
    component: DatePicker,
    name: 'expirationDate',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'PDF',
    requiredLabel: false,
    component: PdfFileInput,
    name: 'fileUrl',
  },
  {
    lg: 2,
    md: 6,
    sm: 12,
    label: 'Notas',
    component: TextInput,
    type: 'text',
    name: 'note',
  },
];

const DRIVER_CONTRACT_FORM_COLUMNS = [
  {
    lg: 3,
    md: 3,
    sm: 6,
    label: 'Tipo de Contrato',
    requiredLabel: true,
    component: DriverContractTypeSelect,
    name: 'driverContractTypeId',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
    },
  },
  {
    lg: 2,
    md: 3,
    sm: 6,
    label: 'Fecha de Expedición',
    requiredLabel: true,
    component: DatePicker,
    name: 'effectiveDate',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 3,
    sm: 6,
    label: 'Fecha de Expiración',
    component: DatePicker,
    name: 'expirationDate',
  },
  {
    lg: 2,
    md: 3,
    sm: 6,
    label: 'Razón de terminación',
    component: TextInput,
    type: 'text',
    name: 'reason',
  },
  {
    lg: 3,
    md: 12,
    sm: 12,
    label: 'Notas',
    component: TextInput,
    type: 'text',
    name: 'note',
  },
];

const CONTRACT_ITINERARY_FORM_COLUMNS = [
  // First row
  {
    lg: 3,
    md: 3,
    sm: 6,
    label: 'Tipo de Movimiento',
    requiredLabel: true,
    component: ContractMovementTypeSelect,
    name: 'movementType',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
    },
  },
  {
    lg: 2,
    md: 3,
    sm: 6,
    label: 'Número de Bus',
    component: RegisteredBusSelect,
    name: 'registeredBusId',
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: true,
    },
  },
  {
    lg: 4,
    md: 3,
    sm: 6,
    label: 'Ruta',
    component: RouteSelect,
    name: 'routeId',
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: true,
      placeholder: 'Seleccione...',
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 6,
    label: 'Tipo de Servicio',
    requiredLabel: true,
    component: ServiceTypeSelect,
    name: 'serviceTypeId',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
      placeholder: 'Seleccione...',
    },
  },
  // Second row
  {
    lg: 3,
    md: 8,
    sm: 6,
    label: 'Dirección de Origen',
    component: TextInput,
    type: 'text',
    name: 'sourceAddress',
  },
  {
    lg: 3,
    md: 4,
    sm: 6,
    label: 'Embarque',
    requiredLabel: true,
    component: DatePicker,
    name: 'departureTime',
    validate: [isRequired],
    otherProps: { time: true },
  },
  {
    lg: 3,
    md: 8,
    sm: 6,
    label: 'Dirección de Destino',
    component: TextInput,
    type: 'text',
    name: 'destinationAddress',
  },
  {
    lg: 3,
    md: 4,
    sm: 6,
    label: 'Desembarque',
    requiredLabel: true,
    component: DatePicker,
    name: 'arrivalTime',
    validate: [isRequired],
    otherProps: { time: true },
  },
  // Third row
  {
    lg: 3,
    md: 3,
    sm: 6,
    label: 'Conductor 1',
    component: DriverSelect,
    name: 'firstDriverId',
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: true,
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 6,
    label: 'Conductor 2',
    component: DriverSelect,
    name: 'secondDriverId',
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: true,
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 6,
    label: 'Conductor 3',
    component: DriverSelect,
    name: 'thirdDriverId',
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: true,
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 6,
    label: 'Cantidad Servicio a Bordo',
    component: TextInput,
    type: 'text',
    name: 'cabinCrewQuantity',
    validate: [validateIntegerNumber],
  },
  // Fourth row
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Descripción del Servicio',
    component: TextInput,
    type: 'textarea',
    name: 'routeDetail',
  },
  {
    lg: 2,
    md: 2,
    sm: 4,
    label: 'Paradas',
    component: TextInput,
    type: 'text',
    name: 'authorizedStop',
    validate: [validateIntegerNumber],
  },
  {
    lg: 2,
    md: 2,
    sm: 4,
    label: 'Kilometraje',
    component: TextInput,
    type: 'text',
    name: 'kilometers',
    validate: [validateNumber],
  },
  {
    lg: 2,
    md: 2,
    sm: 4,
    label: 'Dotación',
    component: TextInput,
    type: 'text',
    name: 'expectedFuelConsumption',
    validate: [validateNumber],
  },
];

const generateManualVoucherFormColumns = (
  onChangeQuantity,
  onChangeUnitaryPrice,
) => [
  {
    lg: 2,
    md: 2,
    sm: 3,
    label: 'Cantidad',
    requiredLabel: true,
    component: TextInput,
    onChange: onChangeQuantity,
    type: 'text',
    name: 'quantity',
    validate: [isRequired, validateIntegerNumber],
  },
  {
    lg: 6,
    md: 6,
    sm: 9,
    label: 'Descripción',
    requiredLabel: true,
    component: TextInput,
    type: 'textarea',
    name: 'description',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 2,
    sm: 3,
    label: 'Precio',
    requiredLabel: true,
    component: TextInput,
    onChange: onChangeUnitaryPrice,
    type: 'text',
    name: 'unitaryPrice',
    validate: [isRequired, validateNumber],
  },
  {
    lg: 2,
    md: 2,
    sm: 3,
    label: 'Total',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'totalPrice',
    validate: [isRequired],
    otherProps: {
      disabled: true,
    },
  },
];

const ACTIVITY_MATERIAL_FORM_COLUMNS = [
  {
    lg: 8,
    md: 8,
    sm: 12,
    label: 'Material',
    requiredLabel: true,
    component: MaterialSelect,
    name: 'materialId',
    validate: [
      isRequired,
      ({ value }, allValues) =>
        validateDynamicFormDuplicateElements(
          value,
          allValues.materialList,
          'materialId',
        ),
    ],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
    },
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Cantidad',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    name: 'quantity',
    validate: [validateNumber],
    otherProps: {
      placeholder: '1',
    },
  },
];

const BILL_ITEM_LIST_FORM_COLUMNS = [
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'ID',
    requiredLabel: true,
    component: TextInput,
    validate: [validateNumber],
    name: 'id',
  },
  {
    lg: 3,
    md: 3,
    sm: 3,
    label: 'Peso',
    requiredLabel: true,
    component: TextInput,
    validate: [validateNumber],
    name: 'weight',
  },
  {
    lg: 7,
    md: 7,
    sm: 7,
    label: 'Descripción',
    requiredLabel: true,
    component: TextInput,
    name: 'description',
  },
];

const BILL_DRIVER_LIST_FORM_COLUMNS = [
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Tipo Doc.',
    requiredLabel: true,
    component: Select,
    name: 'documentType',
    otherProps: { options: enumToSelectOptions(BILLING_DOCUMENT_TYPE) },
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Núm. Doc.',
    requiredLabel: true,
    component: TextInput,
    name: 'documentNumber',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Nombre',
    requiredLabel: true,
    component: TextInput,
    name: 'name',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Apellido',
    requiredLabel: true,
    component: TextInput,
    name: 'lastName',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Licencia',
    requiredLabel: true,
    component: TextInput,
    name: 'driverAuthorization',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Tipo',
    component: TextInput,
    name: 'driverType',
  },
];

const generateMaintenanceServiceOrderListFormColumns = (
  noOptionsMessage,
  registeredBusId,
) => [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Actividad',
    component: ActivityMaintenancePlanningSelect,
    name: 'maintenancePlanningActivity',
    noOptionsMessage,
    validate: [isRequired],
    otherProps: {
      registeredBusId,
      cleanWhenUnmount: false,
    },
  },
];

const generateActivityTaskFormColumns = (options) => [
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Tarea',
    component: MaintenanceTaskSelect,
    name: 'maintenanceTask',
    validate: [isRequired],
  },
  {
    lg: 8,
    md: 8,
    sm: 12,
    label: 'Materiales asignados a la tarea',
    requiredLabel: true,
    component: Select,
    name: 'materialId',
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
      isMulti: true,
      options,
    },
  },
];

const generateMaintenanceWarningColumns = (
  hasEqualWarning,
  registeredBusId,
  typeLabel,
  searchByCompanyResource,
) => [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: `Ocurrencia ${typeLabel}`,
    requiredLabel: true,
    component: MaintenanceWarningSelect,
    name: 'maintenanceWarningId',
    validate: [isRequired, hasEqualWarning],
    otherProps: {
      registeredBusId,
      searchByCompanyResource,
    },
  },
];

const validateLength6 = validateLength(6);
const DISTRICT_ITEM_LIST_FORM_COLUMNS = [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Nombre',
    requiredLabel: true,
    component: TextInput,
    name: 'name',
    validate: [isRequired],
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Ubigeo',
    requiredLabel: true,
    component: TextInput,
    name: 'ubigeo',
    validate: [isRequired, validateNumber, validateLength6],
  },
];

const generateProcessRoutesFormColumns = (mobilizing) => [
  {
    label: 'Ruta',
    requiredLabel: true,
    component: RouteSelect,
    name: 'routeId',
    validate: [
      isRequired,
      ({ value }, allValues) =>
        validateDynamicFormDuplicateElements(
          value,
          allValues.routes,
          'routeId',
        ),
    ],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
      mobilizing,
    },
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Hora de salida',
    requiredLabel: true,
    component: TimeInput,
    name: 'departureTime',
    validate: [isRequired],
  },
  {
    label: 'Cantidad de pasajeros',
    requiredLabel: true,
    component: TextInput,
    name: 'passengerCount',
    validate: [isRequired, validateIntegerNumber],
  },
];

const REGISTERED_BUS_DOCUMENT_COLUMNS = [
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Tipo de Documento',
    requiredLabel: true,
    component: DocumentTypeSelect,
    name: 'documentType',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Número de Serie',
    requiredLabel: true,
    component: TextInput,
    name: 'serialNumber',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Fecha de Expedición',
    requiredLabel: true,
    component: DatePicker,
    name: 'effectiveDate',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 3,
    sm: 12,
    label: 'Fecha de Expiración',
    requiredLabel: true,
    component: DatePicker,
    name: 'expirationDate',
    validate: [isRequired],
  },
];

const MAINTENANCE_PLANNING_COLUMNS = [
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Actividad',
    component: ActivitySelect,
    name: 'activity',
    validate: [isRequired],
  },
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Factor para ejecutar',
    component: TextInput,
    name: 'factor',
    validate: [validateNumber],
  },
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Orden',
    component: TextInput,
    name: 'activityOrder',
    validate: [isRequired, validateNumber],
  },
];

const SEVICE_ORDER_TASK_EXECUTION_COLUMNS = [
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Estado',
    component: Select,
    name: 'taskStatus',
    validate: [isRequired],
    otherProps: { options: enumToSelectOptions(ACTIVITY_STATUS).splice(1, 2) },
  },
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'KM a Agregar',
    component: TextInput,
    name: 'taskExtensionFactor',
    validate: [isRequired],
  },
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Personal',
    component: MechanicSelect,
    name: 'taskMechanics',
    validate: [isRequired],
    otherProps: { isMulti: true },
  },
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Fecha/Hora Inicio',
    component: TextInput,
    name: 'taskStartDate',
    type: 'datetime-local',
    placeholder: { DATE_TIME_FORMAT },
  },
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Fecha/Hora Final',
    component: TextInput,
    name: 'taskEndDate',
    type: 'datetime-local',
    placeholder: { DATE_TIME_FORMAT },
    validate: [isRequired],
  },
  {
    lg: 4,
    md: 6,
    sm: 6,
    label: 'Observaciones',
    component: TextInput,
    name: 'taskComment',
  },
];

const MANUAL_GRR_DRIVER_COLUMNS = [
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Tipo Doc.',
    requiredLabel: true,
    component: Select,
    name: 'documentType',
    otherProps: { options: enumToSelectOptions(BILLING_DOCUMENT_TYPE) },
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Número de Documento',
    requiredLabel: true,
    component: TextInput,
    name: 'documentNumber',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Nombre',
    component: TextInput,
    requiredLabel: true,
    name: 'name',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Apellido',
    requiredLabel: true,
    component: TextInput,
    name: 'lastName',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Licencia',
    requiredLabel: true,
    component: TextInput,
    name: 'driverAuthorization',
    validate: [isRequired],
  },
];

const ATTACHED_DOCUMENT_PARCEL_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Documento Relacionado',
    component: TextInput,
    name: 'attachedDocument',
    validate: [isRequired],
  },
];

const CARGO_CORPORATE_CREDIT_CITY_COLUMNS = [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Personal corporativo',
    requiredLabel: true,
    component: CustomerSelect,
    name: 'customer',
    validate: [isRequired],
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Tipo de Personal Corporativo',
    name: 'customerType',
    requiredLabel: true,
    component: Select,
    validate: [isRequired],
    otherProps: {
      options: enumToSelectOptions(CARGO_CUSTOMER_TYPE),
    },
  },
];

const CABIN_CREW_DOCUMENT_FORM_COLUMNS = [
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Tipo de Documento',
    requiredLabel: true,
    component: CabinCrewDocumenTypesSelect,
    validate: [isRequired],
    name: 'cabinCrewDocumentType',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Número',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateNumber],
    name: 'documentNumber',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Fecha de Expedición',
    requiredLabel: true,
    component: DatePicker,
    name: 'effectiveDate',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Fecha de Expiración',
    requiredLabel: true,
    component: DatePicker,
    name: 'expirationDate',
    validate: [isRequired],
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'PDF',
    requiredLabel: false,
    component: PdfFileInput,
    name: 'fileUrl',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Notas',
    component: TextInput,
    name: 'note',
  },
];

const CABIN_CREW_CONTRACT_FORM_COLUMNS = [
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Tipo de Contrato',
    requiredLabel: true,
    component: CabinCrewContractTypesSelect,
    validate: [isRequired],
    name: 'cabinCrewContractType',
  },
  {
    lg: 3,
    md: 3,
    sm: 3,
    label: 'Fecha de Expedición',
    requiredLabel: true,
    component: DatePicker,
    name: 'effectiveDate',
    validate: [isRequired],
  },
  {
    lg: 3,
    md: 3,
    sm: 3,
    label: 'Fecha de Expiración',
    component: DatePicker,
    name: 'expirationDate',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Razón de Terminación',
    component: TextInput,
    name: 'reason',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Notas',
    component: TextInput,
    name: 'note',
  },
];

const ROUTE_SEGMENT_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Segmento',
    requiredLabel: true,
    component: SegmentSelect,
    name: 'segmentId',
    validate: [isRequired],
  },
];

const BUS_MAINTENANCE_STAY_FORM_COLUMNS = [
  {
    lg: 1,
    md: 1,
    sm: 1,
    label: 'Bus',
    component: RegisteredBusSelect,
    name: 'registeredBusId',
    validate: [isRequired],
    requiredLabel: true,
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Ciudad de Origen',
    component: CitySelect,
    name: 'originCityId',
    validate: [isRequired],
    requiredLabel: true,
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Ciudad de Destino',
    component: CitySelect,
    name: 'destinationCityId',
    validate: [isRequired],
    requiredLabel: true,
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Fecha de Llegada',
    component: BusMaintenanceStayDateField,
    name: 'arrivalDateGroup',
  },
  {
    lg: 2,
    md: 2,
    sm: 2,
    label: 'Seleccionar',
    name: 'select',
    component: 'input',
    type: 'checkbox',
    otherProps: { className: 'mt-10 ml-4 scale-1' },
  },
  {
    lg: 1,
    md: 1,
    sm: 1,
    component: TextInput,
    name: 'itineraryId',
    type: 'hidden',
  },
];

const DISCOUNT_CODE_EXCLUDE_DATES_COLUMNS = [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'De',
    requiredLabel: true,
    component: DatePicker,
    name: 'dateFrom',
    validate: [isRequired],
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Hasta',
    requiredLabel: true,
    component: DatePicker,
    name: 'dateUntil',
    validate: [isRequired],
  },
];

const DISCOUNT_CODE_TRIP_RULE_COLUMNS = [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Tipo de Servicio',
    requiredLabel: true,
    component: ServiceTypeSelect,
    name: 'serviceTypeId',
    validate: [isRequired],
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Piso',
    requiredLabel: true,
    component: Select,
    name: 'discountFloorRule',
    otherProps: { options: enumToSelectOptions(DISCOUNT_CODE_FLOOR_RULE) },
    validate: [isRequired],
  },
];

const DISCOUNT_CODE_SUB_CATEGORY_COLUMNS = [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Nombre',
    requiredLabel: true,
    component: TextInput,
    name: 'name',
    validate: [isRequired],
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Autorizadores',
    requiredLabel: true,
    component: DiscountCodeAuthorizerSelect,
    name: 'discountCodeSubCategoryAuthorizerList',
    validate: [isRequired],
    otherProps: {
      isMulti: true,
    },
  },
];

const FREE_OF_CHARGE_SUB_CATEGORY_COLUMNS = [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Nombre',
    requiredLabel: true,
    component: TextInput,
    name: 'name',
    validate: [isRequired],
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Autorizadores',
    requiredLabel: true,
    component: CustomerSelect,
    name: 'freeOfChargeSubCategoryUserList',
    validate: [isRequired],
    otherProps: {
      isMulti: true,
    },
  },
];

const DELIVERY_LOCATION_DISTRICT_COLUMN = [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Ubicación',
    component: LocationSelect,
    name: 'location',
    requiredLabel: true,
    validate: [isRequired],
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Sectores de Distritos',
    requiredLabel: true,
    component: DistrictPostalCodeSelect,
    name: 'districtPostalCodeIds',
    otherProps: {
      isMulti: true,
      closeMenuOnSelect: false,
    },
  },
];

const isMigratedBankAccountRow = (rowValues) =>
  !!rowValues &&
  rowValues.migrationStatus === DEPOSIT_MIGRATION_STATUS.MIGRATED.value;

const generateValidateVerifiedBbvaDeposit = (listName) => (
  value,
  allValues,
  props,
  name,
) => {
  const match = new RegExp(`^${listName}\\[(\\d+)\\]`).exec(name);

  if (!match) return undefined;

  const index = Number(match[1]);
  const row = allValues[listName] && allValues[listName][index];

  if (
    row &&
    !isMigratedBankAccountRow(row) &&
    row.bankAccountId &&
    row.bankAccountId.bankId === BBVA_BANK_ID &&
    !value
  ) {
    return 'Debe verificar el depósito de esta cuenta BBVA antes de guardar';
  }

  return undefined;
};

const verifiedBbvaDepositValidators = {};

const getValidateVerifiedBbvaDeposit = (listName) => {
  if (!verifiedBbvaDepositValidators[listName]) {
    verifiedBbvaDepositValidators[listName] =
      generateValidateVerifiedBbvaDeposit(listName);
  }

  return verifiedBbvaDepositValidators[listName];
};

const generateBankAccountColumns = ({
  onClickVerify,
  onChangeVerifiableField,
  verifyingIndex,
  listName = 'depositBankAccountList',
}) => {
  const isRowReadOnly = (rowValues) =>
    !!(rowValues && rowValues.verified) || isMigratedBankAccountRow(rowValues);

  const validateVerifiedBbvaDeposit = getValidateVerifiedBbvaDeposit(listName);

  return [
    {
      lg: 4,
      md: 4,
      sm: 12,
      label: 'Cuenta Bancaria',
      requiredLabel: true,
      component: BankAccountSelect,
      name: 'bankAccountId',
      validate: [isRequired],
      onChange: onChangeVerifiableField,
      otherProps: {
        cleanWhenUnmount: false,
        isClearable: false,
        isDisabled: isRowReadOnly,
      },
    },
    {
      lg: 2,
      md: 2,
      sm: 12,
      label: 'Número de Operación',
      requiredLabel: true,
      component: TextInput,
      validate: [validateNumber, isRequired],
      name: 'operationNumber',
      onChange: onChangeVerifiableField,
      disabled: isRowReadOnly,
    },
    {
      lg: 2,
      md: 2,
      sm: 12,
      label: 'Monto depositado',
      requiredLabel: true,
      component: TextInput,
      validate: [validateNumber, isRequired],
      name: 'actualAmountDeposited',
      disabled: isRowReadOnly,
    },
    {
      lg: 2,
      md: 2,
      sm: 12,
      label: 'Fecha de depósito',
      requiredLabel: true,
      component: DatePicker,
      validate: [isRequired],
      name: 'depositDate',
      onChange: onChangeVerifiableField,
      disabled: isRowReadOnly,
      otherProps: {
        time: true,
      },
    },
    {
      lg: 2,
      md: 2,
      sm: 12,
      name: 'migrationStatus',
      customizedField: 'custom',
      hidden: (rowValues) => !isMigratedBankAccountRow(rowValues),
      render: () => (
        <span>
          <EnumStatus
            enumObject={DEPOSIT_MIGRATION_STATUS}
            enumValue={DEPOSIT_MIGRATION_STATUS.MIGRATED.value}
          />
          <span className="d-block text-muted" style={{ fontSize: '.7rem' }}>
            No editable
          </span>
        </span>
      ),
    },
    {
      lg: 2,
      md: 2,
      sm: 12,
      name: 'verifyButton',
      customizedField: 'custom',
      hidden: (rowValues) =>
        !rowValues.bankAccountId ||
        rowValues.bankAccountId.bankId !== BBVA_BANK_ID ||
        isMigratedBankAccountRow(rowValues),
      render: (index, rowValues) => {
        if (rowValues.verified) {
          return (
            <span className="text-success">
              <i className="fa fa-check-circle" /> Verificado
            </span>
          );
        }

        const isRowVerifying = verifyingIndex === index;
        const missingFields =
          !rowValues.bankAccountId ||
          !rowValues.operationNumber ||
          !rowValues.depositDate;
        const disabled = verifyingIndex !== null || missingFields;

        return (
          <Button
            type="button"
            color="warning"
            disabled={disabled}
            onClick={() => onClickVerify(index)}
          >
            {isRowVerifying ? (
              <>
                <i className="fa fa-spinner fa-spin" /> Verificando...
              </>
            ) : (
              'Verificar'
            )}
          </Button>
        );
      },
    },
    {
      lg: 12,
      md: 12,
      sm: 12,
      name: 'verified',
      component: TextInput,
      type: 'hidden',
      validate: [validateVerifiedBbvaDeposit],
      hidden: (rowValues) =>
        !rowValues.bankAccountId ||
        rowValues.bankAccountId.bankId !== BBVA_BANK_ID ||
        isMigratedBankAccountRow(rowValues),
    },
  ];
};

const BANK_ACCOUNT_COLUMNS = [
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Cuenta Bancaria',
    requiredLabel: true,
    component: BankAccountSelect,
    name: 'bankAccountId',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Número de Operación',
    requiredLabel: true,
    component: TextInput,
    validate: [validateNumber, isRequired],
    name: 'operationNumber',
  },
  {
    lg: 2,
    md: 2,
    sm: 12,
    label: 'Monto depositado',
    requiredLabel: true,
    component: TextInput,
    validate: [validateNumber, isRequired],
    name: 'actualAmountDeposited',
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Fecha de depósito',
    requiredLabel: true,
    component: DatePicker,
    validate: [isRequired],
    name: 'depositDate',
    otherProps: {
      time: true,
    },
  },
];

const PICKUP_SENDER_REMISSION_GUIDE_ITEM_COLUMNS = [
  {
    lg: 1,
    md: 2,
    sm: 12,
    label: 'Cantidad',
    component: TextInput,
    name: 'quantity',
    type: 'text',
    disabled: true,
  },
  {
    lg: 3,
    md: 4,
    sm: 12,
    label: 'Descripción',
    component: TextInput,
    name: 'description',
    type: 'text',
    disabled: true,
  },
  {
    lg: 2,
    md: 4,
    sm: 12,
    label: 'Fecha de Recepción',
    requiredLabel: true,
    name: 'returnDate',
    component: DatePicker,
  },
  {
    lg: 2,
    md: 4,
    sm: 12,
    label: 'Observación',
    requiredLabel: true,
    name: 'observation',
    component: TextInput,
    type: 'text',
  },
  {
    lg: 2,
    md: 4,
    sm: 12,
    label: 'Estado ',
    requiredLabel: true,
    name: 'status',
    component: Select,
    otherProps: {
      options: enumToSelectOptions(SENDER_REMISSION_GUIDE_ITEM_PICKUP_STATUS),
    },
  },
  {
    lg: 2,
    md: 4,
    sm: 12,
    label: 'Cierre',
    requiredLabel: true,
    name: 'closureStatus',
    component: Select,
    otherProps: {
      options: enumToSelectOptions(SENDER_REMISSION_GUIDE_CLOSURE_STATUS),
    },
  },
];

const generateMaintenanceWarningCompanyResourceColumnColumns = (
  hasEqualPreventiveWarning,
  hasEqualCorrectiveWarning,
  hasOnlyOneWarningSelected,
  registeredBusId,
  validateMyEndDate,
) => [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: `Ocurrencia Preventiva`,
    requiredLabel: true,
    component: MaintenanceWarningSelect,
    name: 'maintenanceWarningId',
    validate: [hasOnlyOneWarningSelected, hasEqualPreventiveWarning],
    otherProps: {
      searchByCompanyResource: true,
      registeredBusId,
      useSearch: true,
      isClearable: true,
      onlyPreventive: true,
    },
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: `Ocurrencia Correctiva`,
    requiredLabel: true,
    component: MaintenanceWarningSelect,
    name: 'correctiveMaintenanceWarningId',
    validate: [hasOnlyOneWarningSelected, hasEqualCorrectiveWarning],
    otherProps: {
      searchByCompanyResource: true,
      registeredBusId,
      useSearch: true,
      onlyCorrective: true,
      isClearable: true,
    },
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: `Fecha/Hora Inicio`,
    requiredLabel: true,
    component: TextInput,
    name: 'startDate',
    validate: [isRequired],
    type: 'datetime-local',
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: `Fecha/Hora Final`,
    requiredLabel: true,
    component: TextInput,
    name: 'endDate',
    validate: [isRequired, validateMyEndDate],
    type: 'datetime-local',
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: `Personal`,
    requiredLabel: true,
    component: MechanicSelect,
    name: 'mechanics',
    validate: [isRequired],
    otherProps: {
      isMulti: true,
    },
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: `Archivo Adjunto`,
    component: FileInput,
    name: 'attachedFile',
    otherProps: {
      accept: '.pdf,.jpg,.png',
      multiple: false,
    },
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: `Monto Gastado`,
    component: TextInput,
    name: 'spentAmount',
    type: 'textInput',
    validate: [validateNumber],
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: `Observaciones`,
    component: TextInput,
    name: 'comment',
    type: 'textarea',
  },
];

const DRIVER_COMMISSION_FORM_COLUMNS = [
  {
    label: 'Condición de Trabajo / KM',
    name: 'commission',
    component: TextInput,
    validate: [validateNumber, isRequired],
    requiredLabel: true,
  },
  {
    label: 'Inicio (KM)',
    name: 'startRange',
    component: TextInput,
    validate: [validateNumber, isRequired],
    requiredLabel: true,
  },
  {
    label: 'Tipo de Conductor',
    name: 'driverType',
    component: DriverTypeSelect,
    otherProps: { cleanWhenUnmount: false, isClearable: true },
  },
];

export const generateIncidentRecordColumns = (
  nodes,
  onNodePathChange,
  nodePathsPerRow,
  allowDuplicateNodes = false,
  label = 'Tipo de incidencia',
) => [
  {
    lg: 12,
    md: 12,
    sm: 12,
    name: 'nodeSelector',
    component: IncidentNodeSelectorDynamicField,
    otherProps: {
      nodes,
      nodePathsPerRow,
      callbackRef: { onNodePathChange },
      allowDuplicateNodes,
      label,
    },
  },
];

const PRICING_PROFILE_DATE_RANGE_COLUMNS = [
  {
    lg: 4,
    md: 4,
    sm: 4,
    label: 'Desde',
    requiredLabel: true,
    component: DatePicker,
    name: 'startDate',
  },
  {
    lg: 4,
    md: 4,
    sm: 4,
    label: 'Hasta',
    requiredLabel: true,
    component: DatePicker,
    name: 'endDate',
  },
  {
    lg: 4,
    md: 4,
    sm: 4,
    label: 'Motivo',
    requiredLabel: true,
    component: TextInput,
    name: 'reason',
  },
];

const SUPPLIER_FOOD_TYPE_COLUMNS = [
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Tipo de Alimento',
    requiredLabel: true,
    component: FoodSelect,
    validate: [isRequired],
    name: 'food',
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Costo Unitario',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateNumber],
    name: 'cost',
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Tipo de Público',
    requiredLabel: true,
    component: Select,
    name: 'publicType',
    otherProps: {
      options: enumToSelectOptions(FOOD_PUBLIC_TYPE),
    },
  },
];

const ITINERARY_SCHEDULE_FOOD_COLUMNS = [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Proveedor',
    requiredLabel: true,
    component: SupplierSelect,
    validate: [isRequired],
    name: 'supplierId',
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Tipo de Público',
    requiredLabel: true,
    component: Select,
    name: 'publicType',
    otherProps: {
      options: enumToSelectOptions(FOOD_PUBLIC_TYPE),
      isMulti: true,
    },
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Tipo de Alimentación',
    requiredLabel: false,
    component: SupplierFoodSelect,
    name: 'foodIds',
    otherProps: {
      isMulti: true,
    },
  },
  {
    lg: 3,
    md: 6,
    sm: 12,
    label: 'Fecha de entrega',
    requiredLabel: true,
    component: DatePicker,
    name: 'scheduleDeliveredDate',
    validate: [isRequired],
  },
];

const ADDITIONAL_ITINERARY_FOOD_COLUMNS = [
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Alimentación',
    requiredLabel: true,
    component: FoodSelect,
    validate: [isRequired],
    name: 'foodId',
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Tipo de Público',
    requiredLabel: true,
    component: Select,
    name: 'publicType',
    otherProps: {
      options: enumToSelectOptions(FOOD_PUBLIC_TYPE),
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Cantidad',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateNumber],
    name: 'quantity',
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Precio',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateNumber],
    name: 'price',
  },
];

const ITINERARY_SUPPLIER_FOOD_TYPE_COLUMNS = [
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Tipo de Alimento',
    requiredLabel: true,
    component: FoodSelect,
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isDisabled: true,
    },
    name: 'food',
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Cantidad',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateNumber],
    name: 'quantity',
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Costo Unitario',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateNumber],
    name: 'unitCost',
    disabled: true,
  },
];

const PREVENTIVE_TASK_CHECKLIST_ITEM_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Descripción',
    requiredLabel: true,
    component: TextInput,
    name: 'name',
  },
];

const generatePreventiveTaskResourceExecutionColumns = (validateMyEndDate) => [
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Recurso',
    requiredLabel: true,
    component: CompanyResourceSelect,
    validate: [isRequired],
    name: 'resourceId',
    otherProps: {
      cleanWhenUnmount: false,
      isDisabled: true,
    },
  },
  {
    lg: 2,
    md: 2,
    sm: 12,
    label: 'Inicio',
    component: TextInput,
    validate: [isRequired],
    type: 'datetime-local',
    name: 'startedDate',
  },
  {
    lg: 2,
    md: 2,
    sm: 12,
    label: 'Culminación',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateMyEndDate],
    type: 'datetime-local',
    name: 'completedDate',
  },
  {
    lg: 2,
    md: 2,
    sm: 12,
    label: 'Estado',
    requiredLabel: true,
    component: Select,
    validate: [isRequired],
    name: 'status',
    otherProps: {
      options: enumToSelectOptions(ACTIVITY_STATUS),
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Comentario',
    component: TextInput,
    name: 'comment',
  },
];

const generatePreventiveTaskSameDateResourceColumns = () => [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Recurso',
    requiredLabel: true,
    component: Select,
    validate: [isRequired],
    name: 'resourceId',
    otherProps: {
      cleanWhenUnmount: false,
      isDisabled: true,
    },
  },
];

const generatePaymentSplitColumns = ({
  paymentMethodOptions,
  paymentMethodLoading,
  onMethodChange,
  selectedMethodIds,
  showVoucherField = false,
  onCreditNoteChange,
  allowExclusiveMethods,
  freeOfChargeSubCategories,
  freeOfChargeUsers,
  onFreeOfChargeCategoryChange,
  onFreeOfChargeSubCategoryChange,
}) => [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Método de pago',
    name: 'paymentMethod',
    component: Select,
    validate: [isRequired],
    onChange: onMethodChange,
    otherProps: {
      isClearable: false,
      options: (rowValues) =>
        paymentMethodOptions.filter((option) => {
          if (
            EXCLUSIVE_TRANSACTION_CODES.includes(option.transactionCode) &&
            !allowExclusiveMethods
          ) {
            return false;
          }
          return (
            (rowValues &&
              rowValues.paymentMethod &&
              rowValues.paymentMethod.value === option.value) ||
            !selectedMethodIds.includes(option.value)
          );
        }),
      isLoading: paymentMethodLoading,
    },
  },
  {
    lg: showVoucherField ? 3 : 6,
    md: showVoucherField ? 3 : 6,
    sm: 12,
    label: 'Monto',
    requiredLabel: true,
    name: 'amount',
    component: TextInput,
    type: 'number',
    placeholder: '0.00',
    validate: [isRequired, validateNumber, validateMinNumber(0)],
    hidden: (rowValues) => {
      const code = getRowTransactionCode(rowValues);
      return (
        code === TRANSACTION_TYPE_CREDIT_NOTE || code === TRANSACTION_TYPE_OTHER
      );
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Nro Referencia (POS)',
    name: 'voucherCode',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    placeholder: 'Código de Voucher',
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_CREDIT_CARD,
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Notas de crédito',
    name: 'creditNotes',
    component: CreditNoteSelect,
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_CREDIT_NOTE,
    onChange: onCreditNoteChange,
    otherProps: {
      isMulti: true,
      isCustomerIdRequired: true,
      optionsRegistered: true,
    },
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Categoría de Servicio a título gratuito',
    name: 'freeOfChargeCategoryId',
    component: FreeOfChargeCategorySelect,
    requiredLabel: true,
    validate: [isRequired],
    onChange: onFreeOfChargeCategoryChange,
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_OTHER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'SubCategoría',
    name: 'freeOfChargeSubCategory',
    component: Select,
    requiredLabel: true,
    validate: [isRequired],
    onChange: onFreeOfChargeSubCategoryChange,
    otherProps: { options: freeOfChargeSubCategories },
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_OTHER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Autorizador',
    name: 'freeOfChargeUser',
    component: Select,
    requiredLabel: true,
    validate: [isRequired],
    otherProps: { options: freeOfChargeUsers },
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_OTHER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Cuenta Bancaria',
    name: 'bankAccount',
    component: BankAccountSelect,
    requiredLabel: true,
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_BANK_TRANSFER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Número de Operación',
    name: 'voucherCode',
    component: TextInput,
    type: 'text',
    placeholder: 'Número de operación',
    requiredLabel: true,
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_BANK_TRANSFER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Fecha de Depósito',
    name: 'depositDate',
    component: DatePicker,
    placeholder: 'Fecha de depósito',
    requiredLabel: true,
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_BANK_TRANSFER,
  },
];

const MAINTENANCE_FAIL_MODE_CATEGORY_COLUMNS = [
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Descripción',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired],
    name: 'name',
  },
];

const MAINTENANCE_BASIC_CHECKLIST_COLUMNS = [
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Actividad Básica',
    component: TextInput,
    name: 'organizationChart',
    otherProps: {
      disabled: true,
    },
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Fecha/Hora Inicio',
    requiredLabel: true,
    component: TextInput,
    name: 'startDate',
    validate: [isRequired],
    type: 'datetime-local',
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Fecha/Hora Final',
    requiredLabel: true,
    component: TextInput,
    name: 'endDate',
    validate: [isRequired],
    type: 'datetime-local',
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Personal',
    requiredLabel: true,
    component: MechanicSelect,
    name: 'maintenancePlanningActivityExecutionEmployeeList',
    validate: [isRequired],
    otherProps: {
      isMulti: true,
    },
  },
  {
    lg: 12,
    md: 12,
    sm: 12,
    label: 'Observaciones',
    component: TextInput,
    name: 'observations',
    type: 'textarea',
  },
];

const EDIT_ITINERARY_SUPPLIER_FOOD_COLUMNS = [
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Tipo de Alimento',
    requiredLabel: true,
    component: FoodSelect,
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isDisabled: true,
    },
    name: 'food',
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Cantidad',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateNumber],
    type: 'text',
    name: 'quantity',
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Costo Unitario',
    component: TextInput,
    type: 'text',
    name: 'unitCost',
    disabled: true,
  },
  {
    lg: 3,
    md: 3,
    sm: 12,
    label: 'Costo Total',
    requiredLabel: true,
    component: TextInput,
    validate: [isRequired, validateNumber],
    type: 'text',
    name: 'cost',
    disabled: true,
  },
];

const generateCabinCrewAssignmentColumns = (onCabinCrewChange) => [
  {
    label: 'Tripulante',
    requiredLabel: true,
    component: CabinCrewSelect,
    onChange: onCabinCrewChange,
    name: 'cabinCrew',
    validate: [isRequired],
    otherProps: { cleanWhenUnmount: false },
  },
];

export const generateHolidayColumns = () => [
  {
    label: 'Fecha',
    requiredLabel: true,
    component: TextInput,
    name: 'holidayDate',
    type: 'date',
    validate: [isRequired],
  },
  {
    label: 'Nombre',
    requiredLabel: true,
    component: TextInput,
    name: 'name',
    validate: [isRequired],
  },
  {
    label: 'Descripción',
    component: TextInput,
    name: 'description',
    type: 'textarea',
  },
];

const MANUAL_VOUCHER_PAYMENT_COLUMNS = [
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Cuenta Bancaria',
    requiredLabel: true,
    component: BankAccountSelect,
    name: 'bankAccountId',
    validate: [isRequired],
    otherProps: {
      cleanWhenUnmount: false,
      isClearable: false,
    },
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Monto depositado',
    requiredLabel: true,
    component: TextInput,
    name: 'amount',
    validate: [validateNumber, isRequired],
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Fecha de depósito',
    requiredLabel: true,
    component: DatePicker,
    name: 'depositDate',
    validate: [isRequired],
    otherProps: {
      time: true,
    },
  },
];

export {
  generateCargoItemColumns,
  generateInternalTransferItemColumns,
  generateManualGrrColumns,
  generateDriverAssignmentColumns,
  generateCargoItemToAgencyColumns,
  PRINT_STATION_FORM_COLUMNS,
  PRICE_PER_RANGE_DATES_FORM_COLUMNS,
  generatePriceVariationByZonesColumns,
  NOTES_FORM_COLUMNS,
  DAILY_LIQUIDATION_FORM_COLUMNS,
  AGENCY_GROUP_FORM_COLUMNS,
  AGENCY_BANK_CODE_FORM_COLUMNS,
  EXPENSE_STATUS_REMARK_FORM_COLUMNS,
  generateBaggageItemFormColumns,
  generateBaggageTicketFormColumns,
  TOLL_FORM_COLUMNS,
  DRIVER_DOCUMENT_FORM_COLUMNS,
  DRIVER_CONTRACT_FORM_COLUMNS,
  CONTRACT_ITINERARY_FORM_COLUMNS,
  generateManualVoucherFormColumns,
  ACTIVITY_MATERIAL_FORM_COLUMNS,
  BILL_ITEM_LIST_FORM_COLUMNS,
  BILL_DRIVER_LIST_FORM_COLUMNS,
  generateMaintenanceServiceOrderListFormColumns,
  generateActivityTaskFormColumns,
  generateMaintenanceWarningColumns,
  DISTRICT_ITEM_LIST_FORM_COLUMNS,
  generateProcessRoutesFormColumns,
  REGISTERED_BUS_DOCUMENT_COLUMNS,
  MAINTENANCE_PLANNING_COLUMNS,
  SEVICE_ORDER_TASK_EXECUTION_COLUMNS,
  MANUAL_GRR_DRIVER_COLUMNS,
  ATTACHED_DOCUMENT_PARCEL_COLUMNS,
  CARGO_CORPORATE_CREDIT_CITY_COLUMNS,
  CABIN_CREW_DOCUMENT_FORM_COLUMNS,
  CABIN_CREW_CONTRACT_FORM_COLUMNS,
  ROUTE_SEGMENT_COLUMNS,
  BUS_MAINTENANCE_STAY_FORM_COLUMNS,
  DISCOUNT_CODE_EXCLUDE_DATES_COLUMNS,
  DISCOUNT_CODE_TRIP_RULE_COLUMNS,
  DISCOUNT_CODE_SUB_CATEGORY_COLUMNS,
  DELIVERY_LOCATION_DISTRICT_COLUMN,
  generateBankAccountColumns,
  PICKUP_SENDER_REMISSION_GUIDE_ITEM_COLUMNS,
  generateMaintenanceWarningCompanyResourceColumnColumns,
  DRIVER_COMMISSION_FORM_COLUMNS,
  PRICING_PROFILE_DATE_RANGE_COLUMNS,
  FREE_OF_CHARGE_SUB_CATEGORY_COLUMNS,
  generateBaggageCargoItemColumns,
  SUPPLIER_FOOD_TYPE_COLUMNS,
  ITINERARY_SCHEDULE_FOOD_COLUMNS,
  ADDITIONAL_ITINERARY_FOOD_COLUMNS,
  ITINERARY_SUPPLIER_FOOD_TYPE_COLUMNS,
  PREVENTIVE_TASK_CHECKLIST_ITEM_COLUMNS,
  generatePreventiveTaskResourceExecutionColumns,
  generatePreventiveTaskSameDateResourceColumns,
  generatePaymentSplitColumns,
  MAINTENANCE_FAIL_MODE_CATEGORY_COLUMNS,
  MAINTENANCE_BASIC_CHECKLIST_COLUMNS,
  EDIT_ITINERARY_SUPPLIER_FOOD_COLUMNS,
  generateCabinCrewAssignmentColumns,
  MANUAL_VOUCHER_PAYMENT_COLUMNS,
  BANK_ACCOUNT_COLUMNS,
};
